import React from 'react';
import type { Locale } from '@atlassian/jira-common-constants/src/supported-locales';
import { connect } from '@atlassian/jira-react-redux';
import type { ProjectKey } from '@atlassian/jira-shared-types';

import type { CreatedComponent, User } from '../../../common/model';
import CreateModal from '../../../create-modal';
import { addComponentSuccess, addComponentFail } from '../../../state/actions';
import { getLocale, getBaseUrl } from '../../../state/context/selectors';
import {
	getProjectKey,
	getProjectId,
	getProjectDefaultAssignee,
	getProjectLead,
} from '../../../state/project/selectors';
import type { DefaultAssignee } from '../../../state/project/types';
import type { State as AppState } from '../../../state/reducer';
import { cancel } from '../../../state/ui/add/actions';
import { isOpen } from '../../../state/ui/add/selectors';

type Props = {
	locale: Locale;
	baseUrl: string;
	projectKey: ProjectKey;
	projectId: number;
	projectDefaultAssignee: DefaultAssignee | undefined;
	projectLead: User | undefined;
	onClose: () => void;
	onComplete: (component: CreatedComponent) => void;
	onError: () => void;
	isDialogOpen: boolean;
};

function AddModal(props: Props) {
	const {
		locale,
		baseUrl,
		projectId,
		projectKey,
		projectDefaultAssignee,
		projectLead,
		onClose,
		onComplete,
		onError,
		isDialogOpen,
	} = props;

	if (!isDialogOpen) {
		return null;
	}

	return (
		<CreateModal
			locale={locale}
			baseUrl={baseUrl}
			projectId={projectId}
			projectKey={projectKey}
			projectDefaultAssignee={projectDefaultAssignee}
			projectLead={projectLead}
			onClose={onClose}
			onComplete={onComplete}
			onError={onError}
		/>
	);
}

type StateProps = Pick<
	Props,
	| 'locale'
	| 'baseUrl'
	| 'isDialogOpen'
	| 'projectId'
	| 'projectKey'
	| 'projectDefaultAssignee'
	| 'projectLead'
>;

const mapStateToProps = (state: AppState): StateProps => ({
	locale: getLocale(state),
	baseUrl: getBaseUrl(state),
	isDialogOpen: isOpen(state),
	projectId: getProjectId(state),
	projectKey: getProjectKey(state),
	projectDefaultAssignee: getProjectDefaultAssignee(state),
	projectLead: getProjectLead(state),
});

type DispatchProps = Pick<Props, 'onClose' | 'onComplete' | 'onError'>;

const mapDispatchToProps: DispatchProps = {
	onClose: cancel,
	onComplete: addComponentSuccess,
	onError: addComponentFail,
};

export default connect(mapStateToProps, mapDispatchToProps)(AddModal);
